import { algoliaClient, hasFacetValues, formatMoney, parseColorsFromAlgoliaNamedTags, waitForAlgoliaClient, indexRouting } from './algolia/utils';

(async () => {
  // Required for Instantsearch.js
  window.process = {
    env: {
      NODE_ENV: 'production',
    },
  };

  // Checks if the algolia config has loaded before continuing
  try {
    await waitForAlgoliaClient();
  } catch (error) {
    console.error('Error while waiting for algolia:', error);
    return;
  }

  const { facet, currentFacets, clearFacets, products, pagination, sortBy, productCount } = await import('./algolia/widgets/product-list');
  const { default: Cookies } = await import('js-cookie');

  class ProductList extends HTMLElement {
    constructor() {
      super();

      this.algoliaConfig = window.algoliaShopify.config;
      this.type = this.dataset.type;
    }

    async connectedCallback() {
      const client = algoliaClient(this.algoliaConfig.app_id, this.algoliaConfig.search_api_key);

      // No longer used as enabling analytics now
      // const searchClient = {
      //   ...client,
      //   search(requests) {
      //     const newRequests = requests.map((request) => {
      //       // Test for empty string and change `analytics` request parameter to `false`
      //       if (!request.params.query || request.params.query.length === 0) {
      //         request.params.analytics = false;
      //       }
      //       return request;
      //     });

      //     return client.search(newRequests);
      //   },
      // };

      this.search = window.instantsearch({
        indexName: `${this.algoliaConfig.index_prefix}products`,
        insights: {
          insightsInitParams: {
            useCookie: true,
          },
        },
        searchClient: client,
        routing: indexRouting(`${this.algoliaConfig.index_prefix}products`, this.dataset.allowedParams),
      });

      this.config = {
        distinct: Boolean(this.algoliaConfig.show_products),
        facets: {
          list: [],
          widgets: [],
          sortBy: [],
        },
        hitsPerPage: this.algoliaConfig.collections_full_results_hits_per_page - parseInt(this.dataset.minusHits),
      };

      /**
       * If user is logged in, set the authenticated user token for analytics
       */
      if (window.algolia.customer) {
        aa('setAuthenticatedUserToken', `${window.algolia.customer}`);
      } else {
        aa('setAuthenticatedUserToken', undefined);
      }

      this.setupFacets();
      this.setupSortBy();

      this.initWishlist();

      this.addWidgets();

      this.search.start();
    }

    setupFacets() {
      const TYPES_TO_WIDGET = {
        slider: {
          name: 'rangeSlider',
          useDefault: true,
          widget: window.instantsearch.widgets.rangeSlider,
        },
        menu: {
          name: 'menu',
          params: { limit: 10 },
          widget: window.instantsearch.widgets.menu,
        },
        conjunctive: {
          name: 'refinementList',
          params: {
            operator: 'and',
            limit: parseInt(this.dataset.maxFacetValues),
          },
          widget: facet,
        },
        disjunctive: {
          name: 'refinementList',
          params: {
            operator: 'or',
            limit: parseInt(this.dataset.maxFacetValues),
          },
          widget: facet,
        },
      };

      const sortByRefined = (sortFunction) => (a, b) => {
        if (a.refined !== b.refined) {
          if (a.refined) {
            return -1;
          }
          if (b.refined) {
            return 1;
          }
        }
        return sortFunction(a, b);
      };

      // Custom sort functions for refinement list
      const sortFunctions = {
        price_range: sortByRefined((a, b) => {
          if (a.name.length === b.name.length) {
            return a.name.localeCompare(b.name);
          }
          return a.name.length - b.name.length;
        }),
      };

      // Custom display functions for refinement list
      const displayFunctions = {
        price_range: (value) => {
          const valueArr = value.split(':');

          return valueArr.map((e) => formatMoney(Number(e) * 100).replace(/\.\d+/, '')).join(' - ');
        },
      };

      const facetToWidget = (facet) => {
        const widget = TYPES_TO_WIDGET[facet.type];

        const params = {
          ...widget.params,
        };

        params.container = `[class~='list-facet-${facet.escapedName}-container']`;
        params.attribute = facet.name;
        params.templates = {};

        // Define sort by function if specified
        if (sortFunctions[facet.name]) {
          params.sortBy = sortFunctions[facet.name];
        }

        const displayFunction = displayFunctions[facet.name];

        // Transform item values
        params.transformItems = function (items) {
          const transformedItems = items.map((item) => {
            const transformedItem = {
              ...item,
            };

            transformedItem.type = {};
            transformedItem.type[facet.type] = true;

            transformedItem.label = displayFunction ? displayFunction(item.value) : item.value;

            return transformedItem;
          });

          // Order values alphabetically for 'Brand' and 'Category'
          if (facet.name === 'vendor' || facet.name === 'named_tags.category') {
            return transformedItems.sort((a, b) => {
              const labelA = a.label.toLowerCase();
              const labelB = b.label.toLowerCase();

              if (labelA < labelB) {
                return -1;
              }
              if (labelA > labelB) {
                return 1;
              }
              return 0;
            });
          }

          return transformedItems;
        };

        return {
          name: widget.name,
          params,
          widget: widget.widget,
        };
      };

      let algoliaFacets;

      if (this.type === 'collection') {
        algoliaFacets = this.algoliaConfig.collection_facets[this.dataset.collectionId] ? this.algoliaConfig.collection_facets[this.dataset.collectionId] : this.algoliaConfig.collection_facets.default;
      } else {
        algoliaFacets = this.algoliaConfig.facets;
      }

      // Filter out the disabled facets
      const enabledFacets = algoliaFacets.filter((facet) => facet.enabled);

      const facets = enabledFacets.map((facet) => ({
        ...facet,
        escapedName: encodeURIComponent(facet.name),
      }));

      // Some facets may be enabled but actually hidden so filter these out
      const shownFacets = facets.filter((facet) => facet.type !== 'hidden');

      const facetWidgets = shownFacets.map(facetToWidget);

      this.config.facets.list = shownFacets;
      this.config.facets.widgets = facetWidgets;

      this.renderFacets({ list: shownFacets, widgets: facetWidgets });
    }

    renderFacets({ list, widgets }) {
      const facetContainer = this.querySelector('collapsible-element.list-toolbar__collapsible');

      // Render facet buttons
      facetContainer.insertAdjacentHTML(
        'afterbegin',
        `
        ${list
          .map(
            (facet) => `
              <div class="list-toolbar__collapsible-group list-facet-${facet.type} list-facet-${facet.escapedName}" data-collapsible-group>
                <button type="button" class="button list-toolbar__collapsible-trigger" data-collapsible-trigger>
                  ${facet.title}
                  <svg class="icon icon-collapsible icon--xs collapsible-trigger-icon" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" width="24" height="24" viewBox="0 0 24 24">
                    <path fill="currentColor" fill-rule="evenodd" d="M18.14,7.71l-6.14,6.14L5.87,7.71l-1.23,1.22,7.36,7.36,7.36-7.36-1.22-1.22Z"></path>
                  </svg>
                </button>
                <div class="list-toolbar__collapsible-panel" data-collapsible-target>
                  <div class="list-facet-${facet.escapedName}-container site-center"></div>
                </div>
              </div>
            `
          )
          .join('')}
          <div class="list-toolbar__collapsible-group" data-collapsible-group>
            <button type="button" class="button list-toolbar__collapsible-trigger" data-collapsible-trigger>
              Sort by
              <svg class="icon icon-collapsible icon--xs collapsible-trigger-icon" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" width="24" height="24" viewBox="0 0 24 24">
                <path fill="currentColor" fill-rule="evenodd" d="M18.14,7.71l-6.14,6.14L5.87,7.71l-1.23,1.22,7.36,7.36,7.36-7.36-1.22-1.22Z"></path>
              </svg>
            </button>
            <div class="list-toolbar__collapsible-panel" data-collapsible-target>
              <div class="ais-sort-orders-container site-center"></div>
            </div>
          </div>
        `
      );

      // Add widgets to client
      widgets.forEach((widget) => {
        const facetWithPanel = window.instantsearch.widgets.panel({
          hidden: function (options) {
            const hide = !hasFacetValues(options.results, widget.params.attribute);
            const displayStyle = hide ? 'none' : '';

            const parentContainerClassName = '.list-facet-' + encodeURIComponent(widget.params.attribute).replace(/\./g, '\\.').replace(/%/g, '\\%');
            const parentContainer = document.querySelector(parentContainerClassName);

            if (parentContainer) parentContainer.style.display = displayStyle;

            return hide;
          },
        })(widget.widget);

        this.search.addWidgets([facetWithPanel(widget.params)]);
      });

      // Init the event listeners on the added facets
      setTimeout(() => {
        facetContainer.construct();

        this.adjustCollapsible();
      }, 250);

      // Bind toggle filters panel events
      this.querySelectorAll('[data-list-filters-toggle-panel-btn]').forEach((btn) => {
        btn.addEventListener('click', (event) => {
          this.toggleFiltersPanel(event.target.dataset.listFiltersTogglePanelBtn);

          if (event.target.dataset.listFiltersTogglePanelBtn === 'close') {
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          }
        });
      });

      // Bind click on body to close collapsed filters (on desktop)
      document.addEventListener('click', (event) => {
        const collapsibleSelector = 'collapsible-element.list-toolbar__collapsible';
        const filtersCollapsible = document.querySelector(collapsibleSelector);

        if (window.innerWidth >= 768) {
          const isOutside = !event.target.closest(collapsibleSelector);
          if (isOutside) {
            filtersCollapsible.closeAll();
          }
        }
      });
    }

    renderSortBy(sortOrders) {
      this.search.addWidgets([
        sortBy({
          container: document.querySelector('.ais-sort-orders-container'),
          items: sortOrders,
        }),
      ]);
    }

    setupSortBy() {
      const base = `${this.algoliaConfig.index_prefix}products`;

      let availableSortOrders;

      if (this.type === 'collection') {
        availableSortOrders = this.algoliaConfig.collection_sort_orders[this.dataset.collectionId] ? this.algoliaConfig.collection_sort_orders[this.dataset.collectionId] : this.algoliaConfig.collection_sort_orders.default;
      } else {
        availableSortOrders = this.algoliaConfig.sort_orders;
      }

      const sortOrders = [
        {
          value: base,
          label: 'Relevance',
        },
      ];

      availableSortOrders.forEach((sortOrder) => {
        if (sortOrder.asc && sortOrder.asc.active) {
          sortOrders.push({
            value: `${base}_${sortOrder.key}_asc`,
            label: sortOrder.asc.title,
          });
        }

        if (sortOrder.desc && sortOrder.desc.active) {
          sortOrders.push({
            value: `${base}_${sortOrder.key}_desc`,
            label: sortOrder.desc.title,
          });
        }
      });

      this.config.facets.sortBy = sortOrders;

      this.renderSortBy(sortOrders);
    }

    addWidgets() {
      // Config
      const config = {
        clickAnalytics: true,
        facetingAfterDistinct: true,
        attributesToSnippet: [],
        attributesToHighlight: [],
        attributesToRetrieve: ['inventory_quantity', 'inventory_management', 'inventory_policy', 'handle', 'title', 'id', 'price_ratio', 'price', 'compare_at_price', 'vendor', 'product_image', 'image', 'variants_min_price', 'variants_max_price', 'variant_title', 'variants_inventory_count', 'named_tags', 'meta'],
      };

      const filters = [];

      if (this.type === 'collection') {
        // Set the collection id to show products from that collection
        filters.push(`collection_ids:"${this.dataset.collectionId}"`);

        // Hits per page isn't fixed here due to collection callouts
        config.hitsPerPage = this.config.hitsPerPage;

        config.ruleContexts = [this.dataset.collectionHandle];

        config.analytics = true;
        config.analyticsTags = ['categoryPage'];
      } else {
        // Set the search query
        config.query = this.dataset.query;

        // Hits per page is fixed to 48 here
        config.hitsPerPage = 48;

        config.analytics = true;
        config.analyticsTags = ['searchPage'];
      }

      let stockPolicy = false;

      if (this.algoliaConfig.stock_policy) {
        if (this.algoliaConfig.stock_policy === 'deny') {
          stockPolicy = 'inventory_quantity > 0';
        } else if (this.algoliaConfig.stock_policy === 'continue') {
          stockPolicy = 'inventory_available:true';
        }
      }

      // Add the stock policy to the filters array
      if (stockPolicy) filters.push(stockPolicy);

      // Add filters to the config
      if (filters.length) config.filters = filters.join(' AND ');

      /**
       * Set the user token, this uses the Shopify Customer ID if logged in,
       * otherwise the anonymous token algolia creates and stores as a cookie is used
       */
      config.userToken = window.algolia.customer ? window.algolia.customer : Cookies.get('_ALGOLIA');

      // Enable personalisation
      config.enablePersonalization = true;

      this.search.addWidgets([window.instantsearch.widgets.configure(config)]);

      // Product List
      const colorsMap = this.dataset.colours
        ? this.dataset.colours.split(',').reduce((colors, colorStr) => {
            const parts = colorStr.split('|');
            if (parts[1]) {
              colors[parts[0]] = parts[1];
            }
            return colors;
          }, {})
        : {};

      this.search.addWidgets([
        products({
          container: document.querySelector('.list-products'),
          type: this.type,
          productList: this,
          transformItems: (products) =>
            products.map((product) => {
              const colors = parseColorsFromAlgoliaNamedTags(product, colorsMap);

              return {
                ...product,
                _distinct: this.config.distinct,
                can_order: product.inventory_management !== 'shopify' || product.inventory_policy === 'continue' || product.inventory_quantity > 0,
                queryID: product.__queryID,
                productPosition: product.__position,
                index: `${this.algoliaConfig.index_prefix}products`,
                colors,
              };
            }),
        }),
      ]);

      // Pagination
      this.search.addWidgets([
        pagination({
          container: document.querySelector('.list-pagination'),
        }),
      ]);

      // Product Count
      this.search.addWidgets([
        productCount({
          container: document.querySelector('.list-toolbar__count'),
          text: 'product',
        }),
      ]);

      // Search query title count
      if (this.type === 'search') {
        this.search.addWidgets([
          productCount({
            container: document.querySelector('.list-query-title__stats'),
            text: 'result',
          }),
        ]);
      }

      // Current facets
      this.search.addWidgets([
        currentFacets({
          container: document.querySelector('.list-toolbar__current'),
        }),
      ]);

      // Clear facets
      this.search.addWidgets([
        clearFacets({
          container: document.querySelector('.list-toolbar__footer'),
        }),
      ]);
    }

    initWishlist() {
      this.wishlistLoaded = false;
      this.wishlistItems = [];

      const callbackFn = (swat) => {
        window._swat.fetch((results) => {
          this.wishlistItems = results;

          this.wishlistLoaded = true;
        });

        swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, (event) => {
          const product = event?.detail?.d;
          if (!product) return;

          const notification = `
              <div class="swym-notifications-container swymTopLeft">
                <a href="/pages/swym-wishlist" class="swym-notification-wishlist swym-clickable">
                  <div class="swym-product-tile">
                    <button class="swym-close-btn swym-is-button">
                      <svg class="swym-icon icon icon-close icon--xs" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" viewBox="0 0 24 24">
                        <title>Close</title>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M18.1,17.1c0.3,0.3,0.3,0.8,0,1.1s-0.8,0.3-1.1,0L12,13.1l-5.1,5.1c-0.3,0.3-0.8,0.3-1.1,0
                        c-0.3-0.3-0.3-0.8,0-1.1l5.1-5.1L5.9,6.9c-0.3-0.3-0.3-0.8,0-1.1s0.8-0.3,1.1,0l5.1,5.1l5.1-5.1c0.3-0.3,0.8-0.3,1.1,0
                        c0.3,0.3,0.3,0.8,0,1.1l0,0L13.1,12L18.1,17.1z"></path>
                      </svg>
                    </button>
                    <img class="swym-image" src="${product.iu}" alt="">
                    <div class="swym-details-container">
                      <div class="swym-details" role="alert">
                        <strong>${product.dt}</strong> has been added to your wishlist successfully.
                      </div>
                    </div>
                  </div>
                </a>
              </div>
            `;

          const container = document.querySelector('.swym-wishlist-notifications-container');

          container.innerHTML = notification;

          const popup = container.querySelector('.swym-notifications-container');

          // animate in
          setTimeout(() => {
            if (popup) {
              popup.classList.add('swym-showing');
            }
          }, 250);

          // animate out
          setTimeout(() => {
            if (popup) {
              popup.classList.remove('swym-showing');
            }
          }, 6000);

          // animate out on click close btn
          container.querySelector('.swym-notifications-container .swym-close-btn ').addEventListener('click', (e) => {
            e.preventDefault();
            container.querySelector('.swym-notifications-container').classList.remove('swym-showing');
          });
        });
      };

      if (!window.SwymCallbacks) {
        window.SwymCallbacks = [];
      }

      window.SwymCallbacks.push(callbackFn);
    }

    adjustCollapsible() {
      const filtersCollapsible = document.querySelector('collapsible-element.list-toolbar__collapsible');

      const breakpoint = window.matchMedia('(max-width: 768px)');

      const update = (bp) => {
        if (bp.matches) {
          filtersCollapsible.options.closeSiblings = false;
        } else {
          filtersCollapsible.options.closeSiblings = true;
          this.toggleFiltersPanel('close');
        }
      };

      // Add event listener for breakpoint (only runs when the breakpoint matches)
      breakpoint.addEventListener('change', update);

      update(breakpoint);
    }

    /**
     * Toggle mobile filters panel
     * @param {String} action 'open' || 'close'
     * @returns void
     */
    toggleFiltersPanel(action = 'close') {
      const filtersSelector = '.list-toolbar__filters';
      const activeClass = 'list-toolbar__filters--is-active';
      const overflowClass = 'overflow-hidden';
      const facets = document.querySelector(filtersSelector);
      const filtersCollapsible = document.querySelector('collapsible-element.list-toolbar__collapsible');

      switch (action) {
        case 'open':
          facets.classList.add(activeClass);
          document.body.classList.add(overflowClass);

          // If mobile width, open collapsible groups that have filters active
          if (window.innerWidth < 768) {
            const filterGroupSelector = '[data-collapsible-group]';
            const activeFilterSelector = '.ais-RefinementList-item--selected';
            const activeFilters = filtersCollapsible.querySelectorAll(activeFilterSelector);

            activeFilters.forEach((activeFilter) => {
              const filterGroup = activeFilter.closest(filterGroupSelector);
              filtersCollapsible.open(filterGroup);
            });
          }

          break;
        case 'close':
        default:
          facets.classList.remove(activeClass);
          document.body.classList.remove(overflowClass);
          // Close all filter collapsibles
          filtersCollapsible.closeAll();
      }
    }
  }

  customElements.define('product-list', ProductList);
})();
